<template>
  <aside class="aside">
    <image-menu
      :icon="
        mode === canvasModes.moving
          ? '$vuetify.icons.tool-moving-filled'
          : '$vuetify.icons.tool-moving'
      "
      :size="28"
      @click="changeMode(canvasModes.moving)"
    >
      <span>Moving</span>
    </image-menu>
    <div class="divider" :class="{ active: mode === canvasModes.moving }" />
    <template v-if="isCompare">
      <image-menu
        :icon="
          mode === canvasModes.single
            ? '$vuetify.icons.tool-frame-filled'
            : '$vuetify.icons.tool-frame'
        "
        :size="26"
        @click="changeMode(canvasModes.single)"
      >
        <span>Single image</span>
      </image-menu>
      <div class="divider" :class="{ active: mode === canvasModes.single }" />
    </template>
    <image-menu
      :icon="
        mode === canvasModes.drawing
          ? '$vuetify.icons.tool-shape-filled'
          : '$vuetify.icons.tool-shape'
      "
      :close-on-content-click="false"
      :size="24"
      @click="changeMode(canvasModes.drawing)"
    >
      <div class="menu-container">
        <div class="menu-item">
          <v-btn-toggle
            class="toogle-shapes"
            :value="options.sketchType"
            @change="setLineOption('sketchType', $event)"
          >
            <v-btn v-for="item in shapes" :key="item.value" :value="item.value">
              <v-icon size="18">{{ item.icon }}</v-icon>
            </v-btn>
          </v-btn-toggle>
        </div>
        <div class="menu-item">
          <div class="menu-item-header">
            <v-icon size="20">$vuetify.icons.thickness</v-icon>
            <span>Thickness</span>
          </div>
          <v-slider
            color="var(--v-grey-darken-1)"
            hide-details
            max="100"
            min="1"
            track-color="var(--v-grey-lighten-3)"
            track-fill-color="var(--v-grey-lighten-1)"
            :step="1"
            :value="options.lineWidth"
            @change="setLineOption('lineWidth', $event)"
          />
        </div>
        <div class="menu-item">
          <div class="menu-item-header">
            <v-icon size="20">$vuetify.icons.color-swatch</v-icon>
            <span>Color</span>
          </div>
          <v-btn-toggle
            class="toogle-colors"
            :value="options.lineColor"
            @change="setLineOption('lineColor', $event)"
          >
            <v-btn
              v-for="color in colorList"
              :style="{ background: color }"
              :key="color"
              :value="color"
            >
              <v-icon v-if="options.lineColor === color" size="20"
                >$vuetify.icons.check</v-icon
              >
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>
    </image-menu>
    <div class="divider" :class="{ active: mode === canvasModes.drawing }" />
    <image-menu
      v-if="isCompare && imageFilters.length"
      icon="$vuetify.icons.tool-adjustments"
      :close-on-content-click="false"
      :size="24"
    >
      <div class="menu-container">
        <div v-for="(filter, i) in imageFilters" :key="i" class="menu-item">
          <div class="menu-item-header">
            <v-icon size="20">{{ filter.icon }}</v-icon>
            {{ filter.label }}
          </div>
          <v-slider
            color="var(--v-grey-darken-1)"
            hide-details
            max="2"
            min="0.1"
            track-color="var(--v-grey-lighten-3)"
            track-fill-color="var(--v-grey-lighten-1)"
            :step="0.1"
            :value="filter.value"
            @change="filter.handler"
          />
        </div>
      </div>
    </image-menu>
    <template v-if="!isCompare">
      <image-menu
        :icon="
          mode === canvasModes.selecting
            ? '$vuetify.icons.cursor-filled'
            : '$vuetify.icons.cursor'
        "
        :size="24"
        @click="changeMode(canvasModes.selecting)"
      >
        <span>Select line</span>
      </image-menu>
      <div
        class="divider"
        :class="{ active: mode === canvasModes.selecting }"
      />
      <image-menu
        :icon="
          mode === canvasModes.calibration
            ? '$vuetify.icons.compass-filled'
            : '$vuetify.icons.compass'
        "
        :size="24"
        @click="changeMode(canvasModes.calibration)"
      >
        <span>Calibration Line</span>
      </image-menu>
      <div
        class="divider"
        :class="{ active: mode === canvasModes.calibration }"
      />
      <image-menu
        :icon="
          mode === canvasModes.measuring
            ? '$vuetify.icons.ruler-measure-filled'
            : '$vuetify.icons.ruler-measure'
        "
        :size="24"
        @click="changeMode(canvasModes.measuring)"
      >
        <span>Line with Size</span>
      </image-menu>
      <div
        class="divider"
        :class="{ active: mode === canvasModes.measuring }"
      />
      <image-menu
        button-class="icon-trash"
        icon="$vuetify.icons.trash"
        :size="24"
        @click="$emit('remove')"
      >
        <span>Remove object(s)</span>
      </image-menu>
    </template>
    <div v-if="imageFilters.length" class="divider" />
    <div class="aside-bottom">
      <template v-for="(item, i) in actions">
        <image-menu
          :buttonClass="'action-btn'"
          :disabled="item.disabled || false"
          :key="item.tooltip"
          :icon="item.icon"
          :size="24"
          @click="item.handler"
        >
          <span>{{ item.tooltip }}</span>
        </image-menu>
        <div
          v-if="$slots.default || i !== actions.length - 1"
          :key="item.tooltip + '-divider'"
          class="divider"
        />
      </template>
      <slot />
    </div>
  </aside>
</template>

<script>
import { CANVAS_MODES } from "@/constants";
import ImageMenu from "@/views/image/ImageMenu";

export default {
  name: "ImageToolBar",
  components: {
    ImageMenu,
  },
  props: {
    actions: {
      type: Array,
    },
    imageFilters: {
      type: Array,
      default: () => [],
    },
    isCompare: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Object,
      required: true,
    },
  },
  computed: {
    mode() {
      return this.options.mode;
    },
    canvasModes() {
      return this.isCompare
        ? {
            drawing: "drawing",
            moving: "moving",
            single: "single",
          }
        : {
            calibration: CANVAS_MODES.calibration,
            drawing: CANVAS_MODES.drawing,
            measuring: CANVAS_MODES.measuring,
            moving: CANVAS_MODES.moving,
            selecting: CANVAS_MODES.selecting,
            single: CANVAS_MODES.single,
          };
    },
    shapes() {
      return this.isCompare
        ? [
            { icon: "$vuetify.icons.shape-dot", value: "pencil" },
            { icon: "$vuetify.icons.shape-line", value: "line" },
            { icon: "$vuetify.icons.shape-arrow", value: "arrow" },
            { icon: "$vuetify.icons.shape-ellipse", value: "circle" },
          ]
        : [{ icon: "$vuetify.icons.shape-line", value: "line" }];
    },
  },
  data() {
    return {
      colorList: ["red", "green", "blue", "yellow"],
    };
  },
  methods: {
    changeMode(value) {
      this.$emit("changeOption", "mode", value);
    },
    setLineOption(option, value) {
      this.$emit("changeOption", option, value);
      this.$emit("changeOption", "mode", this.canvasModes.drawing);
    },
  },
};
</script>

<style lang="scss" scoped>
.aside {
  min-width: 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px;
  box-shadow: 0px 1px 1px rgba(var(--v-primary-base-rgb), 0.1),
    0px 2px 2px rgba(var(--v-primary-base-rgb), 0.07),
    0px 1px 5px rgba(var(--v-primary-base-rgb), 0.06),
    inset -1px 0px 0px var(--v-blue-lighten-2);
}

.aside-bottom {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: auto;
}

.menu-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.menu-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.menu-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.divider {
  height: 1px;
  width: 100%;
  background: var(--v-blue-lighten-2);
  transition: 0.3s;

  &.active {
    background: var(--v-primary-base);
  }
}

::v-deep {
  .action-btn {
    rect {
      fill: none;
    }
  }

  .toogle-shapes {
    .v-btn {
      min-width: 32px !important;
      width: 32px !important;
      height: 32px !important;
      background: none;
      border: none;
      border-radius: 6px;
    }

    .v-btn--active {
      background: var(--v-grey-lighten-4);
    }
  }

  .toogle-colors {
    gap: 8px;

    .v-btn {
      min-width: 24px !important;
      width: 24px !important;
      height: 24px !important;
      border: none;
      border-radius: 6px;
    }
  }

  .v-slider {
    min-height: 20px;
    margin: 0;
  }

  .v-slider__thumb {
    background-color: white !important;
    border: 2px solid var(--v-grey-darken-1);

    &:before {
      transform: scale(0);
      left: -14px;
      top: -14px;
    }

    &:after {
      width: 16px;
      height: 16px;
    }
  }

  .v-slider__thumb-container--active .v-slider__thumb::before {
    transform: scale(1) !important;
  }

  .v-slider__thumb-container:hover .v-slider__thumb::before {
    transform: scale(0.7);
  }

  .icon-trash {
    path {
      fill: white;
    }
  }
}
</style>
